import CloseIcon from '@mui/icons-material/Close';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

import Button from '@/components/Button';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { Order } from '@/types/fulfill/inventoryHistory';

import PickupsDownloadButton from './dependencies/PickupsDownload/PickupsDownloadButton';

const PICKUP_STATUS_LABELS: Record<string, string> = {
  scheduled: 'En proceso',
  pending: 'Pendiente',
  completed: 'Completado',
  failed: 'Fallido',
  cancelled: 'Cancelado',
  canceled: 'Cancelado',
  inprogress: 'En proceso',
  in_progress: 'En proceso',
};

const translatePickupStatus = (status: string): string =>
  PICKUP_STATUS_LABELS[status.toLowerCase()] ?? status;

interface InfoRowProps {
  label: string;
  value?: string | null;
}

const InfoRow = ({ label, value }: InfoRowProps) => {
  if (!value) return null;
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
      <strong>{label}:</strong> {value}
    </Typography>
  );
};

interface StatusBadgeProps {
  status?: string | null;
}

const StatusBadge = ({ status }: StatusBadgeProps) => (
  <Typography
    variant="body2"
    sx={{
      display: 'inline-block',
      px: 1,
      py: 0.5,
      borderRadius: 1,
      bgcolor: 'grey.100',
      color: 'text.primary',
      fontSize: '0.75rem',
      textTransform: 'capitalize',
    }}
  >
    {status ? translatePickupStatus(status) : 'N/A'}
  </Typography>
);

interface PickupsModalProps {
  open: boolean;
  order: Order | null;
  onClose: () => void;
}

const PickupsModal = ({ open, order, onClose }: PickupsModalProps) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !order?._id) {
      setOrderDetails(null);
      setError(null);
      return;
    }

    const loadOrderDetails = async () => {
      try {
        const orderId = order._id;
        if (!orderId) {
          setError('No se encontró el identificador de la orden.');
          return;
        }
        setLoading(true);
        setError(null);
        const http = initializeHttpProvider();
        const response = await http.historyOrder.orderById(orderId);
        setOrderDetails(response.data);
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error al cargar los detalles de los pickups';
        setError(errorMessage);
        console.error('Error loading order details:', err);
      } finally {
        setLoading(false);
      }
    };

    void loadOrderDetails();
  }, [open, order?._id]);

  if (!order) return null;

  const parcelsCount = order.parcels ? Number(order.parcels) : 0;
  const pickups = orderDetails?.pickups?.created ?? [];
  const pickupsCount = pickups.length;
  const isIncomplete = parcelsCount !== pickupsCount;
  const hasFailed = orderDetails?.pickups?.hasFailed ?? false;
  const taskTracking = orderDetails?.pickups?.taskTracking;

  const originDetail = orderDetails?.pickups?.detail?.origin;
  const warehouseLocation = orderDetails?.warehouse?.location ?? order.warehouse?.location;
  const warehouseName = orderDetails?.warehouse?.name ?? order.warehouse?.name;

  const sortedPickups = [...pickups].sort(
    (a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0),
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Recolección de la orden {order.aoNumber}
            </Typography>
            {taskTracking && (
              <Typography variant="caption" color="text.secondary">
                Total: {taskTracking.total} | Completados: {taskTracking.completed} | Fallidos:{' '}
                {taskTracking.failed} | Pendientes: {taskTracking.pending}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {/* Sección Origen y Destino */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* ORIGEN */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, height: '100%' }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                    <PlaceOutlinedIcon
                      sx={{ width: '25px', height: 'auto', color: theme.palette.primary.main }}
                    />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Origen
                    </Typography>
                  </Stack>

                  {originDetail ? (
                    <Box sx={{ pl: 0.5 }}>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        {originDetail.address}
                      </Typography>
                      <InfoRow label="Nombre" value={order.contact.name} />
                      <InfoRow label="Teléfono" value={originDetail.phone ?? order.contact.phone} />
                      <InfoRow label="Ciudad" value={originDetail.city} />
                      <InfoRow label="Código Postal" value={originDetail.zipcode} />
                      <InfoRow label="Referencias" value={originDetail.reference} />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
                      No hay información de origen disponible
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* DESTINO */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, height: '100%' }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                    <RouteOutlinedIcon
                      sx={{ width: '25px', height: 'auto', color: theme.palette.primary.main }}
                    />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Destino
                    </Typography>
                  </Stack>

                  {warehouseLocation ? (
                    <Box sx={{ pl: 0.5 }}>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        {[
                          warehouseLocation.address,
                          warehouseLocation.city,
                          warehouseLocation.state,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </Typography>
                      <InfoRow label="Almacén" value={warehouseName} />
                      <InfoRow label="Teléfono" value={warehouseLocation.phone} />
                      <InfoRow label="Código Postal" value={warehouseLocation.zipcode} />
                      <InfoRow label="Referencias" value={warehouseLocation.reference} />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
                      No hay información de destino disponible
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Sección de Pickups */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Recolecciones creadas</Typography>
              {pickups.length > 0 && (
                <PickupsDownloadButton
                  trackingIds={sortedPickups.map((p) => String(p.trackingId))}
                />
              )}
            </Stack>

            {pickups.length === 0 ? (
              <Alert severity="warning">
                {hasFailed
                  ? 'Las recolecciones no pudieron ser creadas'
                  : 'No hay recolecciones creadas para esta orden'}
              </Alert>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: 1, borderColor: 'divider' }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell>
                          <strong>#</strong>
                        </TableCell>
                        <TableCell>
                          <strong>ID interna</strong>
                        </TableCell>
                        <TableCell>
                          <strong>N° de seguimiento</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Estado</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedPickups.map((pickup, index) => (
                        <TableRow key={pickup._id ?? index} hover>
                          <TableCell>{pickup.sequenceNumber ?? 'N/A'}</TableCell>
                          <TableCell>{pickup.internalKey ?? 'N/A'}</TableCell>
                          <TableCell>{pickup.trackingId ?? 'N/A'}</TableCell>
                          <TableCell>
                            <StatusBadge status={pickup.status} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {isIncomplete && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    Recolecciones incompletas: Se requieren {parcelsCount} recolección(es) pero solo
                    hay {pickupsCount} creada(s)
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          size="medium"
          sx={{
            boxShadow: 'none',
            '&:focus': { boxShadow: 'none' },
            '&:active': { boxShadow: 'none' },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PickupsModal;
